import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProdutoLocalEstoqueRow {
  id: number;
  localEstoqueId: number;
  localEstoqueDescricao: string;
  unidadeEntradaId: number;
  unidadeSaidaId: number;
  unidadeEntradaTributavelId: number;
  unidadeSaidaTributavelId: number;
  fatorConversaoEntrada: number;
  fatorConversaoSaida: number;
  fatorConversaoEntradaTributavel: number;
  fatorConversaoSaidaTributavel: number;
  vendaFracionada: boolean;
  padraoEntrada: boolean;
  padraoSaida: boolean;
  estoqueMinimo: number;
  estoqueMaximo: number;
  custoMedio: number;
  ultimaCompra: number;
  dtUltimaCompra: string;
  precoReposicao: number;
  precoGerencial: number;
  volume: number;
  unidadeVolume?: string | null;
  peso: number;
  unidadePeso?: string | null;
}

export interface SalvarLocalEstoqueRequest {
  localEstoqueId: number;
  unidadeEntradaId: number;
  unidadeSaidaId: number;
  unidadeEntradaTributavelId: number;
  unidadeSaidaTributavelId: number;
  fatorConversaoEntrada: number;
  fatorConversaoSaida: number;
  fatorConversaoEntradaTributavel: number;
  fatorConversaoSaidaTributavel: number;
  vendaFracionada: boolean;
  padraoEntrada: boolean;
  padraoSaida: boolean;
  estoqueMinimo: number;
  estoqueMaximo: number;
  volume: number;
  unidadeVolume?: string | null;
  peso: number;
  unidadePeso?: string | null;
}

export const useProdutoLocaisEstoque = (produtoId: string | undefined) => {
  const queryClient = useQueryClient();
  const numId = produtoId ? Number(produtoId) : undefined;
  const key = ["produto-locais-estoque", numId];
  const base = `/api/produtos/${numId}/locais-estoque`;

  const query = useQuery({
    queryKey: key,
    queryFn: async () => (await apiClient.get<ProdutoLocalEstoqueRow[]>(base)).data,
    enabled: !!numId,
  });

  const createMutation = useMutation({
    mutationFn: async (body: SalvarLocalEstoqueRequest) => (await apiClient.post<ProdutoLocalEstoqueRow>(base, body)).data,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: key }); toast.success("Configuração de estoque adicionada!"); },
    onError: (e: Error) => toast.error(`Erro ao adicionar: ${e.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SalvarLocalEstoqueRequest }) =>
      (await apiClient.put<ProdutoLocalEstoqueRow>(`${base}/${id}`, data)).data,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: key }); toast.success("Configuração de estoque atualizada!"); },
    onError: (e: Error) => toast.error(`Erro ao atualizar: ${e.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => { await apiClient.delete(`${base}/${id}`); },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: key }); toast.success("Configuração de estoque removida!"); },
    onError: (e: Error) => toast.error(`Erro ao remover: ${e.message}`),
  });

  return {
    locais:      query.data ?? [],
    isLoading:   query.isLoading,
    createLocal: createMutation.mutate,
    updateLocal: updateMutation.mutate,
    deleteLocal: deleteMutation.mutate,
    isSaving:    createMutation.isPending || updateMutation.isPending,
  };
};
